<template>
  <settings-form :breadcrumbs="useSettingsBreadcrumbs()">
    <template #actions>
      <v-btn
        v-if="isLocalePriorityMode || isLocalePriorityDraftMode"
        class="mr-2"
        color="success"
        :disabled="!isChanged"
        @click="save"
      >
        <v-icon>
          mdi-check
        </v-icon>{{ $t('button.save_changes') }}
      </v-btn>
    </template>
    <template #content>
      <v-alert
        v-if="!isLocalePriorityMode && preferencesLoaded"
        type="info"
        border="left"
        prominent
        class="mb-8"
      >
        <v-row align="center">
          <v-col class="grow">
            <div class="text-subtitle-2 font-weight-medium mb-1">
              {{ $t('message.message_center_preferences_upgrade_required_title') }}
            </div>
            <div class="text-body-2">
              {{ isLocalePriorityDraftMode
                ? $t('message.message_center_preferences_setup_in_progress_body')
                : $t('message.message_center_preferences_upgrade_required_body') }}
            </div>
          </v-col>
          <v-col class="shrink">
            <v-btn
              v-if="!isLocalePriorityMode && !isLocalePriorityDraftMode"
              class="mr-2 black--text"
              color="yellow"
              @click="startSetup"
            >
              <v-icon class="mr-2">
                mdi-auto-fix
              </v-icon>
              {{ $t('button.mc_start_setup') }}
            </v-btn>
          </v-col>
        </v-row>
      </v-alert>

      <div class="message-center-preferences__content-wrap">
        <div :class="{'message-center-preferences__content-disabled': !isLocalePriorityMode && !isLocalePriorityDraftMode && preferencesLoaded}">
          <validation-observer ref="observerRef">
            <settings-group
              :title="$t('settings.messagecenter.communication_settings')"
              :subtitle="$t('settings.messagecenter.communication_settings_subtitle')"
              :gutter="$vuetify.breakpoint.lgAndUp"
            >
              <v-col
                cols="12"
                md="6"
                class="py-0 my-0"
              >
                <v-select
                  v-model="commStyle"
                  :items="commStyleItems"
                  :label="$t('label.communication_style')"
                  item-value="id"
                  item-text="name"
                />
              </v-col>
            </settings-group>

            <settings-group
              :title="$t('settings.messagecenter.text_blocks')"
              :subtitle="$t('settings.messagecenter.text_blocks_subtitle')"
              :gutter="$vuetify.breakpoint.lgAndUp"
            >
              <multi-language-group
                :value="selectedLocale"
                :enabled-locales="enabledLocales"
                :translated-locales="enabledLocales.map((l) => l.locale)"
                @input="selectedLocale = $event"
              >
                <v-col
                  cols="12"
                  class="d-flex flex-row align-center"
                >
                  <rich-text-editor-row
                    :value="header"
                    height="20px"
                    :label="$t('label.placeholder_header')"
                    :placeholders="placeholdersHeader"
                    resettable
                    :reset-enabled="headerIsCustom"
                    @reset="resetHeader"
                    @input="header = $event"
                  />
                </v-col>
                <v-col
                  cols="12"
                  class="d-flex flex-row align-center"
                >
                  <rich-text-editor-row
                    :value="footer"
                    height="48px"
                    :label="$t('label.placeholder_footer')"
                    :placeholders="placeholdersFooter"
                    resettable
                    :reset-enabled="footerIsCustom"
                    @reset="resetSignature"
                    @input="footer = $event"
                  />
                </v-col>
                <v-divider class="my-2" />
                <v-col
                  cols="12"
                  class="d-flex flex-row align-center"
                >
                  <rich-text-editor-row
                    :value="fixedEndingTimeNote"
                    :label="$t('label.placeholder_fixed_ending')"
                    height="48px"
                    :placeholders="placeholdersEndTime"
                    resettable
                    :hint="$t('label.help_fixed_ending_time_note')"
                    :reset-enabled="fixedEndingTimeIsCustom"
                    @reset="resetFixedEndingTime"
                    @input="fixedEndingTimeNote = $event"
                  />
                </v-col>
              </multi-language-group>
            </settings-group>
            <settings-group
              :title="$t('settings.messagecenter.advanced_settings')"
              :subtitle="$t('settings.messagecenter.advanced_settings_subtitle')"
              :gutter="$vuetify.breakpoint.lgAndUp"
            >
              <v-col
                cols="12"
                class="py-0 my-0"
              >
                <v-alert
                  v-if="showDetailsInSummary === false"
                  dense
                  border="left"
                  type="warning"
                >
                  {{ $t('message.message_center_details_hidden_from_summary') }}
                </v-alert>
                <v-switch
                  v-model="showDetailsInSummary"
                  :label="$t('label.show_detais_in_email_summary_block')"
                />
              </v-col>
              <v-col
                cols="12"
                class="py-0 my-0"
              >
                <v-switch
                  v-model="showRestaurantPhone"
                  :label="$t('label.email_show_phone_in_footer')"
                />
              </v-col>
              <v-col
                cols="12"
                class="py-0 my-0"
              >
                <v-switch
                  v-model="showRestaurantEmail"
                  :label="$t('label.show_email_in_email_footer')"
                />
              </v-col>
              <v-col
                cols="12"
                class="py-0 my-0"
              >
                <v-switch
                  v-model="showGuestNote"
                  :label="$t('label.show_guest_note_in_emails')"
                />
              </v-col>
            </settings-group>
          </validation-observer>
        </div>

        <div
          v-if="!isLocalePriorityMode && !isLocalePriorityDraftMode && preferencesLoaded"
          class="message-center-preferences__overlay"
        />
      </div>
    </template>
  </settings-form>
</template>

<script setup lang="ts">
/* eslint-disable max-len */
/* eslint-disable vue/max-len */
import {
  computed, getCurrentInstance, onBeforeMount, onBeforeUnmount, ref, watch,
} from 'vue';
import SettingsForm from '@/components/layouts/SettingsForm.vue';
import { useSettingsBreadcrumbs } from '@/mixins/settings-breadcrumbs';
import store from '@/store/store';
import MessageCenterStore from '@/store/message-center-store';
import { loadStoreModule, unloadStoreModule } from '@/services/module-loader';
import { performAction, performDispatchAction, performSaveAction } from '@/services/vue-utils';
import RichTextEditorRow from '@/components/editors/RichTextEditorRow.vue';
import { useHandlebars } from '@/composables/messagecenter';
import MultiLanguageGroup from '@/components/dialogs/MultiLanguageGroup.vue';
import MessageSettings, { CommunicationFlags, CommunicationStyles } from '@/grpc-api/model/message-settings';
import MessageTranslation, { MessageTranslationKey } from '@/grpc-api/model/message-translation';
import { cloneModel, isModelEqualToModel } from '@/model/model-utils';
import { strcmp } from '@/services/common';
import i18nn from '@/plugins/i18n';
import { useSortedLanguages } from './settings/helpers/settings-values';

// data
const preferencesLoaded = ref(false);
let oldSettings = new MessageSettings();
let oldTranslations = [] as MessageTranslation[];
const settings = ref(cloneModel(oldSettings));
const translations = ref(oldTranslations.map((t) => cloneModel(t)));

const isChanged = computed(() => !isModelEqualToModel(settings.value, oldSettings) || !isModelEqualToModel(translations.value, oldTranslations));

const isLocalePriorityMode = computed(() => store.getters.messageCenterIsLocalePriorityMode as boolean);
const isLocalePriorityDraftMode = computed(() => store.getters.messageCenterIsLocalePriorityDraftMode as boolean);

// Communication style
const commStyle = computed({
  get: () => (settings.value?.communicationStyles?.includes(CommunicationStyles.Informal) ? 0 : 1),
  set: (value: number) => { settings.value.communicationStyles = value === 0 ? [CommunicationStyles.Informal] : []; },
});
const commStyleInformal = computed(() => settings.value?.communicationStyles?.includes(CommunicationStyles.Informal) ?? false);
watch(commStyleInformal, (newVal, oldVal) => {
  if (!preferencesLoaded.value) return;
  if (newVal === oldVal) return;
  /* eslint-disable no-param-reassign */
  translations.value.forEach((t) => {
    if (t.textValue === t.defaultFormalTextValue && newVal) t.textValue = t.defaultInformalTextValue;
    else if (t.textValue === t.defaultInformalTextValue && !newVal) t.textValue = t.defaultFormalTextValue;
  });
}, { immediate: false });

const commStyleItems = [
  { id: 0, name: i18nn.t('label.communication_style_informal') },
  { id: 1, name: i18nn.t('label.communication_style_formal') },
];

// Languages
const languages = useSortedLanguages();
const selectedLocale = ref('en');
const enabledLocales = computed(() => {
  const localesMap = store.getters.enabledLocalesSet as Set<string>;
  // the language tabs display locale CODES, so order by code — languages.value is sorted
  // by display name, which makes the codes appear shuffled ("cs, nl, en, de, ...")
  const locales = languages.value.filter((l) => localesMap.has(l.locale)).sort((a, b) => strcmp(a.locale, b.locale));
  console.log('Enabled locales:', locales);
  return locales;
});
// Advanced settings
const changeFlags = (value: boolean, flag: CommunicationFlags) => {
  let flags = settings.value.communicationFlags || [];
  if (!value) flags = flags.filter((f) => f !== flag);
  else if (!flags.includes(flag)) { flags = [...flags, flag]; flags.sort((a, b) => strcmp(a, b)); }
  settings.value.communicationFlags = flags;
};

const showDetailsInSummary = computed({
  get: () => !settings.value?.communicationFlags?.includes(CommunicationFlags.HideSummaryDetails),
  // inverted flag: the switch shows "details visible", the flag stores "hide details"
  set: (value: boolean) => { changeFlags(!value, CommunicationFlags.HideSummaryDetails); },
});

const showRestaurantPhone = computed({
  get: () => !settings.value?.communicationFlags?.includes(CommunicationFlags.HideFooterPhone),
  set: (value: boolean) => { changeFlags(!value, CommunicationFlags.HideFooterPhone); },
});

const showRestaurantEmail = computed({
  get: () => !settings.value?.communicationFlags?.includes(CommunicationFlags.HideFooterEmail),
  set: (value: boolean) => { changeFlags(!value, CommunicationFlags.HideFooterEmail); },
});

// positive flag (opt-in), unlike the inverted hide_* flags above
const showGuestNote = computed({
  get: () => settings.value?.communicationFlags?.includes(CommunicationFlags.ShowGuestNote) ?? false,
  set: (value: boolean) => { changeFlags(value, CommunicationFlags.ShowGuestNote); },
});

// translation
const getTranslation = (key: string) => {
  const translation = translations.value.find((t) => t.locale === selectedLocale.value && t.textKey === key);
  return translation?.textValue ?? 'N/A';
};
const setTranslation = (key: string, value: string) => {
  const translation = translations.value.find((t) => t.locale === selectedLocale.value && t.textKey === key);
  if (translation !== undefined) translation.textValue = value;
};
const resetTranslation = (key: string) => {
  const translation = translations.value.find((t) => t.locale === selectedLocale.value && t.textKey === key);
  if (translation !== undefined) translation.textValue = commStyleInformal.value ? translation?.defaultInformalTextValue : translation?.defaultFormalTextValue;
};

// customized = differs from the current tone's builtin default -> show the reset cross
const isCustomTranslation = (key: string) => {
  const translation = translations.value.find((t) => t.locale === selectedLocale.value && t.textKey === key);
  if (translation === undefined) return false;
  const defaultValue = commStyleInformal.value ? translation.defaultInformalTextValue : translation.defaultFormalTextValue;
  return translation.textValue !== defaultValue;
};
const headerIsCustom = computed(() => isCustomTranslation(MessageTranslationKey.Heading));
const footerIsCustom = computed(() => isCustomTranslation(MessageTranslationKey.Footer));
const fixedEndingTimeIsCustom = computed(() => isCustomTranslation(MessageTranslationKey.FixedEndingTimeNote));

const header = computed({
  get: () => getTranslation(MessageTranslationKey.Heading),
  set: (value: string) => setTranslation(MessageTranslationKey.Heading, value),
});

const footer = computed({
  get: () => getTranslation(MessageTranslationKey.Footer),
  set: (value: string) => setTranslation(MessageTranslationKey.Footer, value),
});

const fixedEndingTimeNote = computed({
  get: () => getTranslation(MessageTranslationKey.FixedEndingTimeNote),
  set: (value: string) => setTranslation(MessageTranslationKey.FixedEndingTimeNote, value),
});

const resetHeader = () => resetTranslation(MessageTranslationKey.Heading);
const resetSignature = () => resetTranslation(MessageTranslationKey.Footer);
const resetFixedEndingTime = () => resetTranslation(MessageTranslationKey.FixedEndingTimeNote);

const placeholders = (textKeys: string[]) => {
  const filtered = useHandlebars.value.filter((o) => textKeys.includes(o.name));
  const mapped = filtered.map((o) => ({ id: o.id, name: i18nn.tc(`code.handlebar.${o.name}`), expression: o.expression }));
  console.log('Placeholders:', useHandlebars.value, filtered, mapped);
  return mapped;
};

const placeholdersHeader = computed(() => placeholders(['full_name']));
const placeholdersFooter = computed(() => placeholders(['restaurant_name']));
const placeholdersEndTime = computed(() => placeholders(['end_time']));

// store operation
const reset = async () => {
  oldSettings = store.getters.messageCenterSettings as MessageSettings;
  oldTranslations = store.getters.messageCenterTranslations as MessageTranslation[];
  settings.value = cloneModel(oldSettings);
  translations.value = oldTranslations.map((t) => cloneModel(t));
  selectedLocale.value = settings.value.defaultLocale;
  preferencesLoaded.value = true;
};

const load = async () => {
  if (!store.getters.isMessageCenterLoaded) {
    const ok = await performAction(
      null,
      i18nn.tc('error.load_message_center'),
      async () => store.dispatch('loadMessageCenter'),
    );
    if (!ok) return;
  }

  reset();
};

const save = async () => {
  if (!preferencesLoaded.value) return;

  const ok = await performSaveAction(
    null,
    async () => store.dispatch('saveMessageCenterPreferences', { settings: settings.value, translations: translations.value }),
  );
  if (!ok) return;

  reset();
};

// hand the setup over to the messages view: the new templates are prepared there, so
// navigate and let it open the start-setup (migration) dialog on arrival (?start-setup)
const vm = getCurrentInstance()?.proxy;
const startSetup = () => {
  vm?.$router.push({ name: 'message-center', query: { 'start-setup': '1' } });
};

onBeforeMount(async () => {
  loadStoreModule(getCurrentInstance()?.proxy!, 'messageCenter', MessageCenterStore, async () => { load(); });
});

onBeforeUnmount(() => {
  unloadStoreModule(getCurrentInstance()?.proxy!, 'messageCenter', async () => { store.dispatch('resetMessageCenter'); });
});

</script>

<style lang="scss">
.message-center-preferences__content-wrap {
  position: relative;
}

.message-center-preferences__content-disabled {
  pointer-events: none;
  user-select: none;
  opacity: 0.55;
  filter: grayscale(0.25);
}

.message-center-preferences__overlay {
  position: absolute;
  inset: 0;
  pointer-events: auto;
  z-index: 5;
}
</style>
